import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import BusinessNavbar from 'components/Navbars/BusinessNavbar'
import ItemTabs from 'components/ItemTabs'
import TextInput from 'components/Inputs/TextInput'
import DateInput from 'components/Inputs/DateInput'
import Select from 'components/Inputs/Select'
import ScheduleList from 'components/Lists/ScheduleList'
import ScheduleDetailsModal from 'pages/Modals/ScheduleDetailsModal'
import EditBillModal from 'pages/Modals/EditBillModal'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Container, FilterContainer, Title } from './styles'
import { DateTime } from 'luxon'

/*
  Online é o que o próprio cliente marcou, pelo link de agendamento do salão ou pelo app Cutilei.
  Cada aba abre na sua ordem; a online, pelo agendamento feito por último, e filtra só por cliente
  e por profissional.
*/
const TABS = [
  { label: 'Todos os agendamentos', value: 'all', sort: { field: 'date', direction: 'asc' } },
  {
    label: 'Agendamentos online',
    value: 'online',
    sort: { field: 'created_at', direction: 'desc' },
    unsortableFields: ['professional', 'date', 'time', 'created_at', 'comission_percentage', 'price']
  }
]

export default function Schedules () {
  const [loading, setLoading] = useState (true)
  const [query, setQuery] = useState ('')
  const [page, setPage] = useState (1)
  const [lastPage, setLastPage] = useState (0)
  const [total, setTotal] = useState (0)
  const [schedules, setSchedules] = useState ([])
  const [scheduleData, setScheduleData] = useState (null)
  const [billData, setBillData] = useState (null)
  const [workingDays, setWorkingDays] = useState ([])
  const [professionals, setProfessionals] = useState ([])
  const [statusList, setStatusList] = useState ([])
  const [status, setStatus] = useState({ label: 'Agendado', value: 'scheduled' })
  const [professionalStatus, setProfessionalStatus] = useState ({
    label: 'Profissionais ativos',
    value: 'active'
  })
  const [professional, setProfessional] = useState (
    JSON.parse (localStorage.getItem ('professional_schedules'))
  )
  const [dateRange, setDateRange] = useState ([
    DateTime.now ().startOf ('month').toISODate (),
    DateTime.now ().toISODate ()
  ])
  const [tab, setTab] = useState(TABS[0].value)
  const [sort, setSort] = useState(TABS[0].sort)
  const [showScheduleDetailsModal, setShowScheduleDetailsModal] = useState (false)
  const [showEditBillModal, setShowEditBillModal] = useState (false)
  const businessId = AuthService.getBusinessId ()
  const isProfessional = AuthService.isProfessional ()
  const token = AuthService.getToken ()
  const isOnline = tab === 'online'

  /*
    A aba online não escolhe entre ativos, inativos e excluídos: a lista de profissionais é sempre a
    dos ativos. A escolha feita em todos fica guardada para a volta.
  */
  const listedProfessionals = isOnline ? 'active' : professionalStatus.value

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [listedProfessionals])

  useEffect (() => {
    setLoading (true)
    getSchedules (1)
  }, [query, status, dateRange, professional, sort, tab])

  const getData = async () => {
    try {
      const professionalFilters = listedProfessionals === 'deleted'
        ? '&deleted=true'
        : `&status=${listedProfessionals}&deleted=false`

      const [
        { data: professionals },
        { data: scheduleStatuses },
        { data: workingDays }
      ] = await Promise.all ([
        cutileiApi.get (
          `/businesses/${businessId}/professionals?access_level=service${professionalFilters}`,
          requestConfig  
        ),
        cutileiApi.get (`/schedule_statuses?business_id=${businessId}`, requestConfig),
        cutileiApi.get (`/businesses/${businessId}/working_days`, requestConfig)
      ])

      setProfessionals (professionals.map (professional => ({
        label: professional.nickname,
        value: professional.id
      })))
      
      setStatusList (scheduleStatuses)
      setWorkingDays (workingDays)
    } catch (error) {
      logApiError (error)
    }
  }

  const getSchedules = async (pageNumber = page, _query = query)  => {
    const startDate = dateRange ? dateRange[0] : null
    const endDate = dateRange ? dateRange[1] : null

    if (lastPage && pageNumber > lastPage) return
    let statusFilters = '&canceled=false'
    if (status.value === 'canceled') statusFilters = '&canceled=true'
    else if (status.value === 'all') statusFilters = '&canceled=all'
    else statusFilters = `&status=${status.value}`
    
    /*
      A aba online só filtra por cliente e por profissional: sem período e sem status, ela traz todo
      agendamento online, cancelado ou não. O período e o status escolhidos em todos ficam guardados
      para a volta, mas não entram na busca dela.
    */
    const dateFilters = isOnline ? '' : `&start_date=${startDate}&end_date=${endDate}`

    const filters = `&query=${encodeURIComponent(_query)}${dateFilters}`
                  + `&professional_id=${professional?.value || ''}`
                  + (isOnline ? '&canceled=all' : statusFilters)
                  + `&sort=${sort.field}&order=${sort.direction}`
                  + (isOnline ? '&online_schedules=true' : '')
                  + (isProfessional ? '&show_all_professionals=true' : '')

    try {
      const { data: schedulesResponse } = await cutileiApi.get (
        `/schedules?business_id=${businessId}&page=${pageNumber}&per_page=25${filters}`,
        requestConfig
      )
      const { data, meta } = schedulesResponse

      pageNumber > 1 ? setSchedules ([...schedules, ...data]) : setSchedules (data)
      setPage (pageNumber + 1)
      setLastPage (meta.last_page)
      setTotal (meta.total)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const handleEditSchedule = schedule => {
    setSchedules (prevSchedules => prevSchedules.map (s => {
      return s.id === schedule.id ? schedule : s
    }))
  }

  const handleEditScheduleStatus = editedSchedules => {
    setSchedules (prevSchedules => prevSchedules.map (schedule => {
      const editedSchedule = editedSchedules.find (s => s.id === schedule.id)
      return editedSchedule ? editedSchedule : schedule
    }))
  }

  /*
    A divisão devolve o agendamento original já alterado e o novo: o primeiro é substituído no
    lugar, o segundo entra no fim da lista, que é paginada e não tem ordenação própria.
  */
  const handleSplitSchedule = splitSchedules => {
    setSchedules (prevSchedules => [
      ...prevSchedules.map (schedule => (
        splitSchedules.find (s => s.id === schedule.id) ?? schedule
      )),
      ...splitSchedules.filter (s => !prevSchedules.some (schedule => schedule.id === s.id))
    ])
  }

  const handleDeleteSchedule = schedule => setSchedules (schedules.filter (s => s.id !== schedule.id))

  /* A mesma coluna inverte o sentido; outra coluna começa do menor para o maior. */
  const handleSort = field => setSort(prevSort => (
    prevSort.field === field
      ? { field, direction: prevSort.direction === 'asc' ? 'desc' : 'asc' }
      : { field, direction: 'asc' }
  ))

  /*
    Trocar de aba volta à ordem em que ela abre; os filtros continuam os mesmos. A aba que já está
    aberta não faz nada: sem o retorno, ela desfaria a ordem escolhida no cabeçalho.
  */
  const handleTabChange = value => {
    if (value === tab) return

    setTab(value)
    setSort(TABS.find(t => t.value === value).sort)
  }

  const toggleScheduleDetailsModal = () => setShowScheduleDetailsModal (!showScheduleDetailsModal)
  const toggleEditBillModal = () => setShowEditBillModal (!showEditBillModal)

  return (
    <Container>
      <BusinessNavbar/>
      <Title>Histórico de agendamentos</Title>
      <ItemTabs
        tabs={TABS}
        tabWidth={180}
        selectedTab={tab}
        onTabChange={handleTabChange}
        containerStyle={{paddingTop: 0, paddingBottom: 15}}
      />
      <FilterContainer $online={isOnline}>
        <TextInput
          value={query}
          placeholder='Pesquisar por cliente'
          isClearable={true}
          onChange={e => setQuery (e.target.value)}
          onClear={() => {
            setQuery ('')
            setLoading (true)
            getSchedules (1, '')
          }}
          inputStyle={{boxShadow: '0px 1px 10px -6px', paddingInline: 12}}
        />
        {!isOnline && (
          <>
            <DateInput
              value={dateRange}
              selectRange={true}
              onChange={setDateRange}
            />
            <Select
              placeholder='Status...'
              value={status}
              options={[
                { label: 'Todos', value: 'all' },
                ...statusList.map(status => ({
                  label: status.name,
                  value: status.codename
                }))
              ]}
              onChange={(_, option) => setStatus(option)}
            />
            <Select
              placeholder='Profissionais...'
              value={professionalStatus}
              options={[
                {label: 'Profissionais ativos', value: 'active'},
                {label: 'Profissionais inativos', value: 'inactive'},
                {label: 'Profissionais excluídos', value: 'deleted'}
              ]}
              onChange={(_, option) => setProfessionalStatus(option)}
            />
          </>
        )}
        <Select
          placeholder='Profissional...'
          value={professional}
          options={professionals}
          isClearable={true}
          onChange={(_, option) => {         
            setProfessional (option)
            localStorage.setItem ('professional_schedules', JSON.stringify (option))
          }}
        />
      </FilterContainer>
      {loading ? <Loading style={{marginTop: 10}}/> : (
        <ScheduleList
          detailed={true}
          schedules={schedules}
          totalSchedules={total}
          hasMore={!(lastPage && page > lastPage)}
          sort={sort}
          unsortableFields={TABS.find(t => t.value === tab).unsortableFields}
          onSort={handleSort}
          onScroll={() => getSchedules ()}
          onScheduleSelected={schedule => {
            setScheduleData (schedule)
            toggleScheduleDetailsModal ()
          }}
        />
      )}
      {scheduleData && (
        <>
          <ScheduleDetailsModal
            visible={showScheduleDetailsModal}
            data={scheduleData}
            schedules={schedules}
            scheduleStatuses={statusList}
            workingDays={workingDays}
            onEdit={schedule => {
              setScheduleData (schedule)
              handleEditSchedule (schedule)
              toggleScheduleDetailsModal ()
            }}
            onEditStatus={schedules => {
              handleEditScheduleStatus (schedules)
              toggleScheduleDetailsModal ()
            }}
            onSplit={schedules => {
              handleSplitSchedule (schedules)
              toggleScheduleDetailsModal ()
            }}
            onDelete={handleDeleteSchedule}
            onClose={toggleScheduleDetailsModal}
            onOpenBill={bill => {
              setBillData (bill)
              setScheduleData ({...scheduleData, bill_id: bill.id})
              toggleEditBillModal ()
            }}
            onEditBill={bill => {
              setBillData (bill)
              toggleScheduleDetailsModal ()
              toggleEditBillModal ()
            }}
          />
          {billData && (
            <EditBillModal
              visible={showEditBillModal}
              data={billData}
              onClose={toggleEditBillModal}
              onDeleteSchedule={handleDeleteSchedule}
              onEditScheduleStatus={handleEditScheduleStatus}
              onCloseBill={() => {
                setScheduleData ({...scheduleData, status: 'paid', validated: true})
              }}
            />
          )}
        </>
      )}
    </Container>
  )
}
